import {
  Alert,
  AsyncButton,
  Button,
  Input,
  RadioGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
  Switch,
  Textarea,
} from '@bpinternal/ui-kit-next'
import { ReactNode, useMemo } from 'react'
import ToggleLightIcon from '~/assets/toggle-light.svg?react'
import ToggleDarkIcon from '~/assets/toggle-dark.svg?react'
import ChatEmbeddedLightIcon from '~/assets/embedded-light.svg?react'
import ChatEmbeddedDarkIcon from '~/assets/embedded-dark.svg?react'
import ChatBubbleLightIcon from '~/assets/chat-bubble-light.svg?react'
import ChatBubbleDarkIcon from '~/assets/chat-bubble-dark.svg?react'
import { CodeSnippet, Paywall } from '~/components'
import { Section } from './sections/Section'
import { SubSection } from './sections/SubSection'
import { getUseConfigStore } from '../stores/useConfigStore'
import { useWebchatEmbedScript } from '~/hooks'
import { ImageInput } from './elements/ImageInput'
import { nanoid } from 'nanoid'
import { RadioCard } from '~/elements/RadioCard'
import { match } from 'ts-pattern'
import { LatestWebchatTheme, DEFAULT_EMBEDDED_CHAT_ID, DEFAULT_TOGGLE_CHAT_ID } from '@bpinternal/shared'
import { MessagePreview } from '@botpress/webchat'
import { WebchatComponentPreview } from './elements/WebchatComponentPreview'
import { useLocalStore } from '~/stores/localStore'
import useNavigationBlocker, { navigationBlockerOptions } from '../hooks/useNavigationBlocker'

type Props = {
  botId: string
  workspaceId: string
}

export function DeploySettingsConfig({ botId, workspaceId }: Props) {
  const { embedScript } = useWebchatEmbedScript({ botId, workspaceId })

  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])
  const embeddedChatId = useConfigStore((state) => state.embeddedChatId)
  const toggleChatId = useConfigStore((state) => state.toggleChatId)
  const botAvatar = useConfigStore((state) => state.botAvatar)
  const fabImage = useConfigStore((state) => state.fabImage)
  const proactiveMessageEnabled = useConfigStore((state) => state.proactiveMessageEnabled)
  const proactiveMessage = useConfigStore((state) => state.proactiveBubbleMessage)
  const delayTime = useConfigStore((state) => state.proactiveBubbleDelayTime)
  const triggerType = useConfigStore((state) => state.proactiveBubbleTriggerType)
  const fabType: 'default' | 'custom' = toggleChatId !== undefined ? 'custom' : 'default'
  const dashboardTheme = useLocalStore((state) => state.getPreferredTheme())
  const color = useConfigStore((state) => state.color)
  const botName = useConfigStore((state) => state.botName)

  const webchatTheme: LatestWebchatTheme = {
    themeMode: dashboardTheme,
    color,
  }

  const setEmbeddedChatId = useConfigStore((state) => state.setEmbeddedChatId)
  const setToggleChatId = useConfigStore((state) => state.setToggleChatId)
  const setFabImage = useConfigStore((state) => state.setFabImage)
  const setProactiveMessageEnabled = useConfigStore((state) => state.setProactiveMessageEnabled)
  const setProactiveMessage = useConfigStore((state) => state.setProactiveBubbleMessage)
  const setDelayTime = useConfigStore((state) => state.setProactiveBubbleDelayTime)
  const setTriggerType = useConfigStore((state) => state.setProactiveBubbleTriggerType)

  const saveConfig = useConfigStore((state) => state.saveConfig)
  const _updating = useConfigStore((state) => state._updating)
  const _originalConfig = useConfigStore((state) => state._originalConfig)

  const interfaceType: 'fab' | 'embed' =
    embeddedChatId !== undefined ? 'embed' : toggleChatId !== undefined ? 'fab' : 'fab'

  const themeIcons = {
    light: {
      Toggle: ToggleLightIcon,
      ChatEmbedded: ChatEmbeddedLightIcon,
      ChatBubble: ChatBubbleLightIcon,
    },
    dark: {
      Toggle: ToggleDarkIcon,
      ChatEmbedded: ChatEmbeddedDarkIcon,
      ChatBubble: ChatBubbleDarkIcon,
    },
  } as const

  const Icon = themeIcons[dashboardTheme]

  const hasPendingChanges =
    embeddedChatId !== _originalConfig?.embeddedChatId ||
    toggleChatId !== _originalConfig?.toggleChatId ||
    fabImage !== _originalConfig?.fabImage ||
    proactiveMessageEnabled !== _originalConfig?.proactiveMessageEnabled ||
    proactiveMessage !== _originalConfig?.proactiveBubbleMessage ||
    delayTime !== _originalConfig?.proactiveBubbleDelayTime ||
    triggerType !== _originalConfig?.proactiveBubbleTriggerType

  useNavigationBlocker(hasPendingChanges, {
    ...navigationBlockerOptions,
    confirmCallback: async () => {
      try {
        await saveConfig()
        return true
      } catch (error) {
        console.log('Failed to save changes', error)
        return false
      }
    },
    cancelCallback: async () => {
      if (_originalConfig) {
        setEmbeddedChatId(_originalConfig.embeddedChatId)
        setToggleChatId(_originalConfig.toggleChatId)
        setFabImage(_originalConfig.fabImage)
        setProactiveMessageEnabled(_originalConfig.proactiveMessageEnabled)
        setProactiveMessage(_originalConfig.proactiveBubbleMessage)
        setDelayTime(_originalConfig.proactiveBubbleDelayTime)
        setTriggerType(_originalConfig.proactiveBubbleTriggerType)
      }
      return false
    },
  })

  return (
    <Section
      name="Deploy Settings"
      actionButton={
        <AsyncButton
          className="hidden h-9 lg:block"
          onClick={saveConfig}
          disabled={_updating || !hasPendingChanges}
          loading={_updating}
        >
          Publish Changes
        </AsyncButton>
      }
      sticky
    >
      <SubSection
        title="Embed Code"
        description={
          <>
            Copy and paste this code on your webpage.{' '}
            <a
              href="https://www.botpress.com/docs/webchat/get-started/embed-in-element#embed-webchat-in-an-element"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Learn more
            </a>
          </>
        }
        className="!flex !flex-col"
      >
        {embedScript === null ? (
          <Alert className="border border-blue-500 bg-blue-100 text-blue-800">
            Your webchat is not yet configured. Please configure it first.
          </Alert>
        ) : embedScript === undefined ? (
          <Skeleton className="h-16 !w-[calc(140%-2rem)] bg-gray-200" />
        ) : (
          <CodeSnippet code={embedScript} />
        )}
      </SubSection>
      <Separator className="my-10" />
      <SubSection title="Chat Interface" description="Choose how you want to add chat to your website.">
        <RadioGroup
          className="flex h-28 w-full flex-row"
          onValueChange={(value: 'fab' | 'embed') =>
            match(value)
              .with('embed', () => {
                if (embeddedChatId === undefined) {
                  setEmbeddedChatId(DEFAULT_EMBEDDED_CHAT_ID)
                }
                setToggleChatId(undefined)
              })
              .with('fab', () => {
                setToggleChatId(undefined)
                setEmbeddedChatId(undefined)
              })
              .exhaustive()
          }
          value={interfaceType}
        >
          <RadioCardWithLabel id="fab" title="Toggle" value="fab">
            <Icon.Toggle />
          </RadioCardWithLabel>

          <RadioCardWithLabel id="embed" title="Embedded" value="embed">
            <Icon.ChatEmbedded />
          </RadioCardWithLabel>
        </RadioGroup>
      </SubSection>

      {interfaceType === 'fab' && (
        <SubSection title="Chat Launcher" description="How should visitors open chat?">
          <RadioGroup
            className="flex h-24 w-full flex-row"
            value={fabType}
            onValueChange={(value: 'custom' | 'default') => {
              match(value)
                .with('custom', () => {
                  if (toggleChatId === undefined) {
                    setToggleChatId(DEFAULT_TOGGLE_CHAT_ID)
                  }
                  setEmbeddedChatId(undefined)
                })
                .with('default', () => {
                  setEmbeddedChatId(undefined)
                  setToggleChatId(undefined)
                })
                .exhaustive()
            }}
          >
            <RadioCardWithLabel id="default" title="Chat Bubble" value="default">
              <Icon.ChatBubble className="flex items-center justify-center self-center" />
            </RadioCardWithLabel>
            <RadioCardWithLabel id="custom" title="Custom Element" value="custom">
              <span className="flex h-8 min-w-24 max-w-28 justify-center self-center rounded-xl bg-primary/10 px-3 py-2 text-xs group-data-[state=checked]:bg-primary">
                Element
              </span>
            </RadioCardWithLabel>
          </RadioGroup>
        </SubSection>
      )}
      {fabType === 'default' && interfaceType === 'fab' && (
        <SubSection title="Button Image" description="Upload an image for the button.">
          <div className="flex w-full justify-end gap-3">
            <Button
              variant="outline"
              size="sm"
              className="self-center border-none shadow-none"
              onClick={() => setFabImage(botAvatar)}
            >
              Use bot avatar
            </Button>
            <ImageInput
              workspaceId={workspaceId}
              botId={botId}
              title="Bot Avatar"
              imgKey={`button-image-${nanoid(6)}`}
              imgUrl={fabImage ?? ''}
              setImgUrl={setFabImage}
            />
          </div>
        </SubSection>
      )}
      {fabType === 'custom' && interfaceType === 'fab' && (
        <SubSection
          title="Element Selector"
          description={
            <>
              Enter the ID of the button or element that launches the chat.{' '}
              <a
                href="https://www.botpress.com/docs/webchat/interact/use-custom-toggle"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Learn more
              </a>
            </>
          }
        >
          <Input
            id="customElementId"
            type="text"
            placeholder="chat-button"
            value={toggleChatId}
            onChange={(e) => setToggleChatId(e.target.value)}
            className="h-10 border-border focus:border-primary focus:ring-primary/20"
          />
        </SubSection>
      )}
      {interfaceType === 'embed' && (
        <SubSection
          title="Where to Show Chat"
          description={
            <>
              Enter the HTML ID of the container where the chat should appear.{' '}
              <a
                href="https://www.botpress.com/docs/webchat/get-started/embed-in-element"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Learn more
              </a>
            </>
          }
        >
          <Input
            id="customElementId"
            type="text"
            placeholder="chat-container"
            value={toggleChatId}
            onChange={(e) => setToggleChatId(e.target.value)}
            className="h-10 border-border focus:border-primary focus:ring-primary/20"
          />
        </SubSection>
      )}
      <Separator className="my-10" />
      <Paywall minimumPlan="plus" analyticsEventName="proactive_message">
        <SubSection
          title="Proactive Message"
          description={
            <>
              A short message that appears above the chat bubble.{' '}
              <a
                href="https://www.botpress.com/docs/webchat/interact/proactive-message-bubble#display-proactive-bubble"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Learn more
              </a>
            </>
          }
        >
          <div className="flex h-full w-full flex-col gap-2">
            <Switch
              className="self-end"
              checked={proactiveMessageEnabled}
              onCheckedChange={setProactiveMessageEnabled}
            />

            <WebchatComponentPreview className="flex flex-col rounded-lg border bg-gray-50 p-2" theme={webchatTheme}>
              <div className="bpReset h-[70px] w-[99%]">
                <MessagePreview description={`${botName ?? 'Bot'} - a few moments ago`}>
                  {proactiveMessage}
                </MessagePreview>
              </div>
              <div className="my-2 size-14 justify-end self-end">
                <Icon.ChatBubble className="flex items-center justify-center self-center" />
              </div>
            </WebchatComponentPreview>
          </div>
        </SubSection>
        {proactiveMessageEnabled && (
          <>
            <div className="my-10" />
            <SubSection title="Message" description="This message will be displayed in a proactive bubble to users.">
              <Textarea
                value={proactiveMessage}
                onChange={(e) => setProactiveMessage(e.target.value)}
                placeholder="Enter your proactive message..."
                className="min-h-[80px] resize-none"
              />
            </SubSection>
            <div className="my-10" />
            <SubSection title="Trigger Type" description="Appears after specific action or after delay.">
              <Select value={triggerType} onValueChange={(value) => setTriggerType(value as 'afterDelay' | 'userIdle')}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trigger type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="afterDelay">After Delay</SelectItem>
                  <SelectItem value="userIdle">After User Idle</SelectItem>
                </SelectContent>
              </Select>
            </SubSection>
            <div className="my-10" />
            <SubSection title="Delay Time">
              <div className="relative w-full">
                <Input
                  value={delayTime}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === '') {
                      setDelayTime(0)
                    } else {
                      const numValue = Number(value)
                      if (!isNaN(numValue) && numValue >= 0) {
                        setDelayTime(numValue)
                      }
                    }
                  }}
                  min={0}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-sm text-gray-500">seconds</span>
                </div>
              </div>
            </SubSection>
          </>
        )}
      </Paywall>
    </Section>
  )
}

function RadioCardWithLabel({
  title,
  children,
  id,
  value,
  className = 'flex-1 cursor-pointer min-w-28 xl:max-w-56',
  labelClassName = 'h-full',
}: {
  title: string
  children: ReactNode
  id: string
  value: string
  className?: string
  labelClassName?: string
}) {
  return (
    <RadioCard id={id} value={value} className={className} labelClassName={labelClassName}>
      <div className="flex flex-col gap-3 px-1">
        {children}
        <span className="text-xs font-normal text-muted-foreground group-data-[state=checked]:text-foreground">
          {title}
        </span>
      </div>
    </RadioCard>
  )
}
